/* Cart Shipping Estimator (Botarhythm) -- CBB Shipping Rates 代替 */
(function () {
  if (window.customElements.get('cart-shipping-estimator')) return;

  const STORAGE_KEY = 'botarhythm:shipping-estimator:zip';
  const AREA_CACHE_KEY = 'botarhythm:shipping-estimator:area';
  const ZIP_PATTERN = /^\d{3}-?\d{4}$/;

  const formatYen = (cents) => {
    const yen = Math.round(cents / 100);
    return '¥' + yen.toLocaleString('ja-JP');
  };

  const normalizeZip = (raw) => {
    const digits = String(raw || '').replace(/[^\d]/g, '');
    if (digits.length !== 7) return null;
    return digits.slice(0, 3) + '-' + digits.slice(3);
  };

  class CartShippingEstimator extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('[data-estimator-form]');
      this.zipInput = this.querySelector('[data-zip-input]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.results = this.querySelector('[data-results]');
      this.hint = this.querySelector('[data-hint]');
      this.details = this.querySelector('[data-estimator-details]');
      this.progressEl = this.querySelector('[data-progress]');
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.zonesEl = this.querySelector('[data-zones]');
      this.zonesNote = this.querySelector('[data-zones-note]');
      this.freeNotice = this.querySelector('[data-free-notice]');
      this.areaWrap = this.querySelector('[data-area-wrap]');
      this.areaLine = this.querySelector('[data-area-line]');
      this.areaNote = this.querySelector('[data-area-note]');
      this.zoneEls = this.querySelectorAll('[data-zone]');

      this.zoneKey = null;       // 訪問者のゾーン (honshu/shikoku/remote)
      this.zoneRateSen = null;   // そのゾーンの基本送料 (銭)
      this.cartClass = null;     // カート内訳の分類 (送料無料/ギフト/通常)
      this._rateRequest = 0;
      this.hasRateResult = false;

      this.context = this.dataset.context || 'page';
      this.countryName = this.dataset.countryName || 'Japan';
      this.countryCode = this.dataset.countryCode || 'JP';
      this.freeThreshold = Number(this.dataset.freeThreshold || 0);
      this.cartTotal = Number(this.dataset.cartTotal || 0);
      // 送料無料商品（お試しセット・定期便）とギフト/壊れ物（¥5,000無料対象外）の商品ハンドル
      this.freeHandles = String(this.dataset.freeHandles || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.fragileHandles = String(this.dataset.fragileHandles || '').split(',').map((s) => s.trim()).filter(Boolean);
      // ロケール 'ja' | 'en' | 'zh'。Liquid の bl_loc を data-locale で受け取る（型B）。
      this.loc = this.dataset.locale || 'ja';
    }

    // 多言語ヘルパー。呼び出し側は t({ ja: '…', en: '…', zh: '…' }) と書く。
    // 位置引数だと3言語目から読めなくなるため辞書で受ける。未定義の言語は日本語へ落とす。
    t(d) {
      return d[this.loc] != null ? d[this.loc] : d.ja;
    }

    connectedCallback() {
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.calculate();
        });
      }
      if (this.zipInput) {
        this.zipInput.addEventListener('input', () => this.onZipInput());
        const saved = this.loadZip();
        if (saved) {
          this.zipInput.value = saved;
        }
      }
      this.render();
      this.refreshCartClass();
      this.subscribeToCartUpdates();
      this.initArea();

      const saved = this.loadZip();
      if (saved && this.cartTotal > 0) {
        this.calculate({ silent: true });
      }
    }

    // ドロワー再描画で切断されたインスタンスの要求とリスナを無効化する。
    disconnectedCallback() {
      this.invalidateRates();
      if (this._boundCartHandler) {
        document.removeEventListener('cart:refresh', this._boundCartHandler);
        document.removeEventListener('cart:update', this._boundCartHandler);
      }
    }

    // カート内訳を分類。送料無料商品(お試し/定期便)・ギフト/壊れ物(¥5,000無料対象外)・通常。
    classifyCartItems(items) {
      let hasFree = false, hasFragile = false, hasGeneral = false, generalSubtotal = 0;
      (items || []).forEach((it) => {
        const h = it.handle;
        if (this.freeHandles.indexOf(h) !== -1) {
          hasFree = true;
        } else if (this.fragileHandles.indexOf(h) !== -1) {
          hasFragile = true;
        } else {
          hasGeneral = true;
          generalSubtotal += Number(it.line_price != null ? it.line_price : it.final_line_price) || 0;
        }
      });
      const allFree = hasFree && !hasFragile && !hasGeneral;
      return { hasFree: hasFree, hasFragile: hasFragile, hasGeneral: hasGeneral, generalSubtotal: generalSubtotal, allFree: allFree };
    }

    async refreshCartClass() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
        this.cartTotal = Number(cart.total_price) || 0;
        this.cartClass = this.classifyCartItems(cart.items);
      } catch (_) { /* noop */ }
      this.render();
    }

    // 訪問者のエリア別送料を出し分ける。
    // ログイン客は Liquid が data-area-source="account" で確定済み → その行をハイライトするだけ。
    // 未ログイン客は IP 地域判定でゾーンを推定し「推定」と明記して表示する。
    initArea() {
      if (!this.areaWrap) return;
      const source = this.areaWrap.getAttribute('data-area-source');
      if (source === 'account') {
        const zone = this.areaWrap.getAttribute('data-area-zone');
        this.setZoneFromKey(zone);
        this.highlightZone(zone);
        this.render();
        return;
      }
      this.detectAreaByIp();
    }

    // 選択ゾーンとその基本送料 (銭) を DOM の価格表記から取り込む
    setZoneFromKey(zoneKey) {
      this.zoneKey = zoneKey;
      const li = this.querySelector('[data-zone="' + zoneKey + '"]');
      const priceEl = li && li.querySelector('.shipping-estimator__zone-price');
      const digits = String((priceEl && priceEl.textContent) || '').replace(/[^\d]/g, '');
      this.zoneRateSen = digits ? Number(digits) * 100 : null;
    }

    zoneForRegion(region) {
      const r = String(region || '').trim();
      const remote = ['Hokkaido', 'Fukuoka', 'Saga', 'Nagasaki', 'Kumamoto', 'Oita', 'Ōita', 'Miyazaki', 'Kagoshima', 'Okinawa'];
      const shikoku = ['Tokushima', 'Kagawa', 'Ehime', 'Kochi', 'Kōchi'];
      if (remote.indexOf(r) !== -1) return 'remote';
      if (shikoku.indexOf(r) !== -1) return 'shikoku';
      return 'honshu'; // 本州は最多かつ最安。未知の県名も本州に寄せる（過小請求を避けるためではなく、判定不能時の穏当な既定）
    }

    async detectAreaByIp() {
      // キャッシュ（7日）で外部API呼び出しを抑える
      try {
        const cached = JSON.parse(localStorage.getItem(AREA_CACHE_KEY) || 'null');
        if (cached && cached.country === 'JP' && cached.zone && (Date.now() - cached.ts) < 7 * 864e5) {
          this.renderIpArea(cached.zone);
          return;
        }
      } catch (_) { /* noop */ }

      const geo = await this.lookupIpRegion();
      if (!geo || geo.country !== 'JP') return; // 日本以外・取得失敗時は何も出さない（表は常時見えている）
      const zone = this.zoneForRegion(geo.region);
      try {
        localStorage.setItem(AREA_CACHE_KEY, JSON.stringify({ zone: zone, country: 'JP', ts: Date.now() }));
      } catch (_) { /* noop */ }
      this.renderIpArea(zone);
    }

    async lookupIpRegion() {
      // 主: geojs.io（HTTPS/CORS/キー不要・country_code+region）。失敗時は ipwho.is。
      try {
        const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { referrerPolicy: 'no-referrer' });
        if (res.ok) {
          const d = await res.json();
          if (d && d.country_code) return { country: d.country_code, region: d.region };
        }
      } catch (_) { /* noop */ }
      try {
        const res = await fetch('https://ipwho.is/', { referrerPolicy: 'no-referrer' });
        if (res.ok) {
          const d = await res.json();
          if (d && d.success && d.country_code) return { country: d.country_code, region: d.region };
        }
      } catch (_) { /* noop */ }
      return null;
    }

    renderIpArea(zoneKey) {
      if (!this.areaWrap) return;
      this.areaWrap.setAttribute('data-area-source', 'ip');
      this.setZoneFromKey(zoneKey);
      this.highlightZone(zoneKey);
      this.render();
    }

    // 配送プロフィールから合算額を推測しない。住所だけでは配送方法も確定しないため、
    // 送料は shipping_rates が返した方法別の金額のみ表示し、テーマの小計は変更しない。
    renderAreaLine() {
      if (this.areaWrap) this.areaWrap.setAttribute('hidden', '');
    }

    invalidateRates() {
      this._rateRequest = (this._rateRequest || 0) + 1;
      if (this.results) this.results.innerHTML = '';
      this.hasRateResult = false;
      this.rateHasFree = false;
      this.setBusy(false);
      this.render();
    }

    highlightZone(zoneKey) {
      if (!this.zoneEls) return;
      this.zoneEls.forEach((el) => {
        if (el.getAttribute('data-zone') === zoneKey) {
          el.classList.add('shipping-estimator__zone--mine');
        } else {
          el.classList.remove('shipping-estimator__zone--mine');
        }
      });
    }

    onZipInput() {
      this.invalidateRates();
      this.setHint(this.t(
        {
        ja: '※ 入力した郵便番号への配送料・配送方法を表示します。',
        en: 'Shows the shipping cost and methods for the postal code you enter.',
        zh: '※ 显示寄送至所输入邮政编码的运费与配送方式。'
      }
      ), 'normal');
    }

    loadZip() {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch (_) {
        return null;
      }
    }

    saveZip(zip) {
      try {
        localStorage.setItem(STORAGE_KEY, zip);
      } catch (_) { /* noop */ }
    }

    setHint(text, tone) {
      if (!this.hint) return;
      this.hint.textContent = text;
      this.hint.dataset.tone = tone || 'normal';
    }

    setBusy(busy) {
      if (this.submitBtn) {
        this.submitBtn.disabled = busy;
        this.submitBtn.textContent = busy
          ? this.t({ ja: '計算中...', en: 'Calculating...', zh: '计算中...' })
          : this.t({ ja: '計算', en: 'Calculate', zh: '计算' });
      }
    }

    async calculate(opts) {
      this.invalidateRates();
      const request = this._rateRequest;
      const silent = !!(opts && opts.silent);
      const raw = this.zipInput ? this.zipInput.value : '';
      const zip = normalizeZip(raw);
      if (!zip || !ZIP_PATTERN.test(zip)) {
        if (!silent) this.setHint(this.t(
          {
          ja: '郵便番号は7桁の数字で入力してください (例: 150-0001)。',
          en: 'Enter a 7 digit postal code (for example 150-0001).',
          zh: '请输入7位数字的邮政编码（例: 150-0001）。'
        }
        ), 'error');
        return;
      }

      this.setBusy(true);
      if (!silent && this.details && !this.details.open) this.details.open = true;

      try {
        const place = await this.lookupAddress(zip);
        if (request !== this._rateRequest || !this.isConnected) return;
        if (!place || !place.province) {
          this.results.innerHTML = '';
          this.setHint(this.t(
            {
            ja: '郵便番号に該当する住所が見つかりません。番号をご確認ください。',
            en: 'No address matches that postal code. Please check the number.',
            zh: '找不到与该邮政编码对应的地址。请确认号码。'
          }
          ), 'error');
          return;
        }
        const rates = await this.fetchRates(zip, place);
        if (request !== this._rateRequest || !this.isConnected) return;
        this.renderRates(rates, place);
        this.saveZip(zip);
        this.setHint(this.t(
          {
          ja: '現在のカート内容とお届け先での送料の目安です。送料はチェックアウトで確定します。',
          en: 'Estimated rates for your current cart and destination. Shipping is confirmed at checkout.',
          zh: '这是当前购物车和收件地址的运费估算。运费在结账时确定。'
        }
        ), 'normal');
      } catch (err) {
        if (request !== this._rateRequest || !this.isConnected) return;
        this.results.innerHTML = '';
        const msg = String(err && err.message ? err.message : err);
        let userHint = this.t(
          {
          ja: 'この郵便番号への配送料を取得できませんでした。送料はチェックアウトで確認できます。',
          en: 'Shipping rates could not be retrieved for this postal code. Rates are available at checkout.',
          zh: '未能获取寄送至该邮政编码的运费。可在结账时查看运费。'
        }
        );
        if (/422/.test(msg)) {
          userHint = this.t(
            {
            ja: 'この郵便番号への配送方法が見つかりません。番号をご確認ください。',
            en: 'No shipping method was found for this postal code. Please check the number.',
            zh: '找不到寄送至该邮政编码的配送方式。请确认号码。'
          }
          );
        } else if (/cart.*empty|no items/i.test(msg)) {
          userHint = this.t({ ja: 'カートに商品が入っていません。', en: 'Your cart is empty.', zh: '购物车中没有商品。' });
        }
        this.setHint(userHint, 'error');
        console.warn('[shipping-estimator]', err);
      } finally {
        if (request === this._rateRequest) this.setBusy(false);
      }
    }

    async lookupAddress(zip) {
      const digits = String(zip || '').replace(/[^\d]/g, '');
      if (digits.length !== 7) return null;
      try {
        const url = 'https://zipcloud.ibsnet.co.jp/api/search?zipcode=' + digits;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const data = await res.json();
        if (data.status !== 200 || !Array.isArray(data.results) || !data.results.length) return null;
        const r = data.results[0];
        return {
          province: r.address1 || '',
          city: r.address2 || '',
          area: r.address3 || '',
        };
      } catch (err) {
        console.warn('[shipping-estimator] zipcloud lookup failed:', err);
        return null;
      }
    }

    async fetchRates(zip, place) {
      const params = new URLSearchParams();
      params.set('shipping_address[zip]', zip);
      params.set('shipping_address[country]', this.countryName);
      if (place && place.province) {
        params.set('shipping_address[province]', place.province);
      }
      if (place && place.city) {
        params.set('shipping_address[city]', place.city);
      }
      const url = '/cart/shipping_rates.json?' + params.toString();
      const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const bodyText = await res.text();
      let body = null;
      try { body = JSON.parse(bodyText); } catch (_) { /* noop */ }
      if (!res.ok) {
        const detail = (body && (body.message || JSON.stringify(body))) || bodyText;
        console.warn('[shipping-estimator] shipping_rates failed:', res.status, detail);
        throw new Error('shipping_rates failed: ' + res.status + ' :: ' + detail);
      }
      return (body && body.shipping_rates) || [];
    }

    renderRates(rates, place) {
      if (!this.results) return;
      this.hasRateResult = true;
      this.rateHasFree = (rates || []).some((r) => Number(r.price) === 0);
      this.render();

      let header = '';
      if (place && place.province) {
        const addr = [place.province, place.city, place.area].filter(Boolean).join(' ');
        header = '<p class="shipping-estimator__address">' + this.t({ ja: 'お届け先: ', en: 'Ship to: ', zh: '收件地址: ' }) + this.escape(addr) + '</p>';
      }

      if (!rates.length) {
        this.results.innerHTML = header +
          '<p class="shipping-estimator__empty">' + this.t(
            {
            ja: 'この住所に対応する配送方法が見つかりません。',
            en: 'No shipping method is available for this address.',
            zh: '找不到适用于该地址的配送方式。'
          }
          ) + '</p>';
        return;
      }

      const items = rates
        .map((r) => {
          const cents = Math.round(Number(r.price) * 100);
          const priceText = cents <= 0
            ? '<span class="shipping-estimator__free">' + this.t({ ja: '送料無料', en: 'Free shipping', zh: '免运费' }) + '</span>'
            : formatYen(cents);
          const delivery = r.delivery_date
            ? '<span class="shipping-estimator__eta">' + this.t({ ja: 'お届け目安: ', en: 'Estimated delivery: ', zh: '预计送达: ' }) + r.delivery_date + '</span>'
            : '';
          return (
            '<li class="shipping-estimator__rate">' +
            '<span class="shipping-estimator__rate-name">' + this.escape(r.presentment_name || r.name) + '</span>' +
            '<span class="shipping-estimator__rate-price">' + priceText + '</span>' +
            delivery +
            '</li>'
          );
        })
        .join('');

      this.results.innerHTML = header +
        '<ul class="shipping-estimator__rates list-unstyled" role="list">' + items + '</ul>';
    }

    escape(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    // 表示の単一エントリ。カート分類・ゾーン・送料無料しきい値を突き合わせて出し分ける。
    render() {
      if (!this.isConnected) return; // 切断された古いインスタンスは描画しない（合計点滅の防止）
      const c = this.cartClass;
      this.renderAreaLine();

      // API結果（郵便番号での方法別見積）があるときは、それを優先して目安の送料表と
      // 商品分類由来の無料案内を出さない。送料無料までの「あと¥X」バーは有料の見積と矛盾しないので
      // 出し続ける。「設定額に達しています」は、見積に ¥0 の方法が無ければ矛盾するので隠す。
      // （2026-10-20 修正: 以前は見積が出るとバーを常に隠し、郵便番号を保存済みの端末では
      //   カートを開いた直後の自動見積でバーが一瞬で消えていた）
      const rated = this.hasRateResult;
      const hide = (el) => { if (el) el.setAttribute('hidden', ''); };
      const show = (el) => { if (el) el.removeAttribute('hidden'); };

      // 1) 商品ポリシーによる目安。住所・配送方法ごとの無料確約ではない。
      if (c && c.allFree) {
        if (rated) hide(this.freeNotice); else show(this.freeNotice);
        hide(this.zonesEl);
        hide(this.progressEl);
        return;
      }
      hide(this.freeNotice);
      if (rated) hide(this.zonesEl); else show(this.zonesEl);

      // 2) ¥5,000 送料無料の判定対象は「通常商品の小計」。ギフト/壊れ物が含まれる場合は
      //    全体が無料にならないため、無料プログレスバーを出さず対象外の注記に切り替える。
      const generalSub = c ? c.generalSubtotal : this.cartTotal;
      if (c && c.hasFragile) {
        if (this.zonesNote) this.zonesNote.textContent = this.t(
          {
          ja: '※ギフトなど一部商品は送料無料の対象外です。お届け先により送料が加算されます。',
          en: 'Some items, gifts among them, are excluded from free shipping. Shipping is added based on the destination.',
          zh: '※ 礼盒等部分商品不适用免运费。运费会依收件地址加收。'
        }
        );
        if (this.progressEl) this.progressEl.setAttribute('hidden', '');
      } else if (this.progressEl && this.freeThreshold) {
        if (this.zonesNote) this.zonesNote.textContent = this.t(
          {
          ja: '通常商品は¥5,000以上で送料無料となる設定です。お届け先や配送方法による送料はチェックアウトで確定します。',
          en: 'Standard items have a free-shipping threshold of ¥5,000. Rates for your destination and delivery method are confirmed at checkout.',
          zh: '普通商品的免运费门槛为¥5,000。具体地址和配送方式的运费在结账时确定。'
        }
        );
        if (c && c.hasGeneral && generalSub >= this.freeThreshold && rated && !this.rateHasFree) {
          this.progressEl.setAttribute('hidden', '');
        } else if (c && c.hasGeneral && generalSub >= this.freeThreshold) {
          this.progressLabel.innerHTML = this.t({
            ja: '通常商品の<strong>送料無料の設定額</strong>に達しています。送料はチェックアウトで確定します。',
            en: 'The <strong>free-shipping threshold</strong> for standard items has been reached. Shipping is confirmed at checkout.',
            zh: '普通商品已达到<strong>免运费门槛</strong>。运费在结账时确定。'
          });
          this.progressFill.style.width = '100%';
          this.progressEl.removeAttribute('hidden');
          if (!rated) this.toggleZones(true);
        } else if (c && c.hasGeneral) {
          const pct = Math.max(4, Math.min(100, Math.round((generalSub / this.freeThreshold) * 100)));
          this.progressLabel.innerHTML = this.t({
            ja: '通常商品の送料無料の設定額まであと <strong>' + formatYen(this.freeThreshold - generalSub) + '</strong> です。',
            en: '<strong>' + formatYen(this.freeThreshold - generalSub) + '</strong> to reach the free-shipping threshold for standard items.',
            zh: '距离普通商品的免运费门槛还差 <strong>' + formatYen(this.freeThreshold - generalSub) + '</strong>。'
          });
          this.progressFill.style.width = pct + '%';
          this.progressEl.removeAttribute('hidden');
          if (!rated) this.toggleZones(false);
        } else {
          this.progressEl.setAttribute('hidden', '');
        }
      }

    }

    toggleZones(freeReached) {
      if (!this.zonesEl) return;
      if (freeReached) {
        this.zonesEl.setAttribute('hidden', '');
      } else {
        this.zonesEl.removeAttribute('hidden');
      }
    }

    async refreshFromCart() {
      if (!this.isConnected) return; // 切断された古いインスタンスは何もしない
      this.invalidateRates();
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!res.ok) return;
        const cart = await res.json();
        this.cartTotal = Number(cart.total_price) || 0;
        this.cartClass = this.classifyCartItems(cart.items);
        this.toggleHidden(cart.item_count === 0);
        this.render();
        if (cart.item_count > 0 && this.zipInput && this.zipInput.value && this.results) {
          this.calculate({ silent: true });
        }
      } catch (_) { /* noop */ }
    }

    toggleHidden(empty) {
      if (empty) {
        this.setAttribute('hidden', '');
      } else {
        this.removeAttribute('hidden');
      }
    }

    subscribeToCartUpdates() {
      const handler = () => this.refreshFromCart();
      this._boundCartHandler = handler; // disconnectedCallback で解除するため保持
      document.addEventListener('cart:refresh', handler);
      document.addEventListener('cart:update', handler);

      if (window.PUB_SUB_EVENTS && window.subscribe) {
        try {
          window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, handler);
        } catch (_) { /* noop */ }
      }

      const origFetch = window.fetch;
      if (origFetch && !origFetch.__estimatorPatched) {
        const self = this;
        const patched = function (input, init) {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          const isCartMutation = /\/cart\/(add|change|update|clear)\.js/.test(url);
          const result = origFetch.apply(this, arguments);
          if (isCartMutation) {
            result.then(() => setTimeout(() => self.refreshFromCart(), 60)).catch(() => { /* noop */ });
          }
          return result;
        };
        patched.__estimatorPatched = true;
        window.fetch = patched;
      }
    }
  }

  customElements.define('cart-shipping-estimator', CartShippingEstimator);
})();
